/**
 * Account settings: profile, session details and account deletion.
 */

import { useEffect, useState } from 'react';
import type { Entitlements, ProfileSummary, QuotaStatus } from '@core/types';
import { Alert, Badge, Field, QuotaMeter, Spinner } from '../components/ui';
import { PageHead } from '../components/Layout';
import { api, ApiError } from '../lib/api';
import { formatDateTime, formatBytes } from '../lib/format';
import { useRouter } from '../lib/router';
import { useSession } from '../lib/session';
import { webConfig } from '../lib/config';

export interface AccountProps {
  profile: ProfileSummary;
  entitlements: Entitlements;
  quota: QuotaStatus;
  onReload: () => void;
}

export function AccountPage({ profile, entitlements, quota, onReload }: AccountProps) {
  const { getToken, signOut, mode } = useSession();
  const { navigate } = useRouter();
  const [displayName, setDisplayName] = useState(profile.displayName ?? '');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setDisplayName(profile.displayName ?? '');
  }, [profile.displayName]);

  return (
    <>
      <PageHead
        title="Account settings"
        description="Your profile, your plan, and the controls for removing your data."
        actions={<QuotaMeter quota={quota} />}
      />

      {message ? <Alert tone="success">{message}</Alert> : null}
      {error ? <Alert tone="error">{error}</Alert> : null}

      <div className="grid grid--2">
        <div className="card">
          <h2>Profile</h2>
          <div className="row" style={{ marginBottom: 12 }}>
            {profile.avatarUrl ? (
              <img
                src={profile.avatarUrl}
                alt=""
                width={44}
                height={44}
                style={{ borderRadius: '50%' }}
              />
            ) : null}
            <div>
              <div>{profile.email ?? 'no email on record'}</div>
              <div className="small faint">Member since {formatDateTime(profile.createdAt)}</div>
            </div>
          </div>

          <Field label="Display name" htmlFor="display-name">
            <input
              id="display-name"
              type="text"
              maxLength={120}
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              placeholder="How you appear in this application"
            />
          </Field>

          <button
            type="button"
            className="button--primary"
            disabled={saving}
            onClick={async () => {
              setSaving(true);
              setMessage(null);
              setError(null);
              try {
                const token = await getToken();
                await api.updateProfile(token, { displayName: displayName.trim() || null });
                setMessage('Your display name was updated.');
                onReload();
              } catch (caught) {
                setError(
                  caught instanceof ApiError ? caught.message : 'The profile could not be updated.',
                );
              } finally {
                setSaving(false);
              }
            }}
          >
            {saving ? <Spinner /> : null}
            Save profile
          </button>
        </div>

        <div className="card">
          <h2>Session</h2>
          <ul className="plain-list">
            <li>
              Sign-in method:{' '}
              <Badge>{mode === 'dev' ? 'local development' : 'Google (Supabase Auth)'}</Badge>
            </li>
            <li>
              API base URL: <code>{webConfig.apiBaseUrl}</code>
            </li>
            <li>
              Plan:{' '}
              <Badge kind={entitlements.planId === 'pro' ? 'pro' : 'default'}>
                {entitlements.displayName}
              </Badge>
            </li>
            <li>
              Input limit: {formatBytes(entitlements.maxInputBytes)} · History:{' '}
              {entitlements.persistentHistory
                ? 'persistent'
                : `${entitlements.historyRetentionDays} days`}
            </li>
          </ul>
          <div className="row">
            <button
              type="button"
              className="button"
              onClick={async () => {
                await signOut();
                navigate('/');
              }}
            >
              Sign out
            </button>
          </div>
          <p className="small faint" style={{ marginTop: 12 }}>
            Signing out clears the session from this browser. Any bring-your-own AI key is already
            gone — it only ever lived in memory for a single diagnosis.
          </p>
        </div>
      </div>

      <div className="card" style={{ borderColor: 'var(--danger)' }}>
        <h2>Delete account</h2>
        <p>
          This permanently deletes every row owned by your account — profile, diagnoses, saved
          specifications, usage counters, subscription cache and share links — and then removes the
          authentication record. It cannot be undone, and any share link you created stops working
          immediately.
        </p>
        <div className="field">
          <label htmlFor="confirm-delete">
            Type <strong>DELETE</strong> to confirm
          </label>
          <input
            id="confirm-delete"
            type="text"
            value={confirmText}
            onChange={(event) => setConfirmText(event.target.value)}
            autoComplete="off"
          />
        </div>
        <button
          type="button"
          className="button--danger"
          disabled={confirmText !== 'DELETE' || deleting}
          onClick={async () => {
            setDeleting(true);
            setError(null);
            try {
              const token = await getToken();
              await api.deleteAccount(token);
              await signOut();
              navigate('/', { replace: true });
            } catch (caught) {
              setError(
                caught instanceof ApiError ? caught.message : 'The account could not be deleted.',
              );
              setDeleting(false);
            }
          }}
        >
          {deleting ? <Spinner /> : null}
          Delete my account and all data
        </button>
      </div>
    </>
  );
}
